// 3rd party libs
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import theme, { themeColors } from 'themes';
import { lowerCase } from 'lodash';

// Utils
import {
  addIdProp,
  addKeyProp,
  updateMultiSelect,
  updateSelect,
  updateValidatedUser,
  transformUserColumnOptions,
  userNameValidation
} from 'pages/UserManagementTwo/components/utils';

// Types
import {
  BulkUser,
  UserGroup,
  UserManagementTableType,
  UserTypes,
  ValidatedUsersResponse
} from 'types/user-management';

// API
import {
  useGetUserManagementV2Query,
  useUpdateUserStatusMutation,
  useUploadValidateUsersDataMutation,
  useGetUserManagementGroupByIdV2Query,
  useImportValidatedBulkUsersMutation,
  useLazyGetUserImportTemplateFileQuery,
  useLazyGetUserListQuery,
  useValidateUserEmailAvailableV2Mutation
} from 'api';
import { usePaginatedQueryOffset } from 'hooks';
import { PAGE_LENGTH } from 'constants/search';
import { useTranslation } from 'react-i18next';
import { TableAction } from 'pages/UserManagementTwo';
import { useDispatch } from 'react-redux';
import { tanStackTableActions } from 'actions/tanStackTable';
import DeleteUser from '../User/DeleteUser';
import ImportUsers from '../User/ImportUsers/Index';
import { ToastMsg } from 'common/components/Toast';
import { DropDownItem } from 'pages/ProteinMachine/MachineConfig/Common/Alerts/FormElementsTypes';
import { DisplayImportedUsers } from './DisplayImportedUsers';
import { DisplayExistingUsers } from './DisplayExistingUsers';
import { MultiValue } from 'react-select';
import UpdateUser, { CloseIcon, Footer } from '../User/UpdateUser';
import { IcoClose } from 'icons/indexed-icons/IcoClose';
import {
  AlertBody,
  StyledButtonRow,
  StyledTitle
} from 'pages/MachineManagement/MachineManagementPopup/MachineImageUploaderStyledComponents';
import { Button, Modal } from 'components';
import { ModalContent } from 'pages/MachineManagement/ReviewAndPublishPage/reviewAndPublish.elements';
import { ModalSize } from 'types';
import IcoWarningNew from 'icons/indexed-icons/IcoWarningNew';
import { ImportIcon } from 'icons/indexed-icons/ImportIcon';
import { DeleteIcon } from 'icons/indexed-icons/DeleteIcon';
import { AddUserIcon } from 'icons/indexed-icons/AddUserIcon';
import { DownloadIcon } from 'icons/indexed-icons/DownloadIcon';
import saveAs from 'file-saver';
import { OptionType } from 'components/DropDownMultiRadioSelect/DropDownMultiRadioSelect';
import { alphaNumericNameWithSpaceAndHyphen } from 'common/helpers/v1';

interface Props {
  onClickButton: (tableType: UserManagementTableType, id?: string, selectUser?: UserTypes) => void;
  group_id?: string;
  isGroupPage?: boolean;
  setShowImportUserBar?: React.Dispatch<React.SetStateAction<boolean>>;
  setDisableImportUserButton?: React.Dispatch<React.SetStateAction<boolean>>;
  importValidatedUsers?: number;
  setIsActiveUserImportMode?: React.Dispatch<React.SetStateAction<boolean>>;
  moveToGroupOnImportMode?: boolean;
  setMoveToGroupOnImportMode?: (option: string) => void;
}

export interface PageSelection {
  selectedItems?: UserGroup[] | UserTypes[];
  pageNumber?: number;
  isPartialSelection?: boolean;
}
export const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.lightGrey1};
  border-top: none;
`;

const StyledModalHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 1.25rem 1.5rem;
  width: 100%;
  align-items: flex-start;
`;
const ModalIconWrapper = styled.div`
  display: flex;
  gap: 1rem;
  margin-right: 1rem;
`;
const ModalCloseButton = styled.div`
  margin-left: auto;
`;
const defaultUserSortState = {
  id: '',
  desc: false
};

const UserManagementTable = ({
  onClickButton,
  group_id,
  isGroupPage,
  setShowImportUserBar,
  setDisableImportUserButton,
  importValidatedUsers,
  setIsActiveUserImportMode,
  moveToGroupOnImportMode,
  setMoveToGroupOnImportMode
}: Props): JSX.Element => {
  const { t } = useTranslation(['fpns']);
  const [selectedGroupId, setSelectedGroupId] = useState(group_id);
  const [selectedTableItems, setSelectedTableItems] = useState<UserTypes[]>([]);
  const [seletedTableItemsByPage, setSeletedTableItemsByPage] = useState<PageSelection[]>([]);
  const [userSearchValue, setUserSearchValue] = useState('');
  const [newValidatedUsers, setNewValidatedUsers] = useState<ValidatedUsersResponse[]>();
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeUserData, setActiveUserData] = useState({});

  // this search state used in group detail page
  // const [searchValue, setSearchValue] = useState('');

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showImportUsersModal, setShowImportUsersModal] = useState<boolean>(false);
  const [isBulkImportMode, setIsBulkImportMode] = useState<boolean>(false);
  const [showExitImportModal, setShowExitImportModal] = useState<boolean>(false);
  const dispatch = useDispatch();

  const [groupId, setGroupId] = useState('');
  const { onPageChange, pageNumber } = usePaginatedQueryOffset();
  const [pageNumberState, setPageNumber] = useState(pageNumber);
  const [getUsersList, { data: result, isFetching: isLoading }] = useLazyGetUserListQuery();

  const { data: groupDetail } = useGetUserManagementGroupByIdV2Query(group_id || '');

  const [updateUserStatus] = useUpdateUserStatusMutation();
  const [uploadUsersFile] = useUploadValidateUsersDataMutation();
  const [importValidateUsers] = useImportValidatedBulkUsersMutation();
  const { data: groupData } = useGetUserManagementV2Query({
    type: lowerCase(UserManagementTableType.GROUP),
    groupId
  });
  const [validateUserEmail] = useValidateUserEmailAvailableV2Mutation();
  const { data: groups } = useGetUserManagementV2Query({
    type: 'group'
  });
  const [downloadTemplateFile] = useLazyGetUserImportTemplateFileQuery();
  const loadUsersList = () => {
    void getUsersList(
      {
        nameSearchQuery: userSearchValue,
        group_id: selectedGroupId,
        limit: PAGE_LENGTH.SMALL,
        offset: pageNumberState
      },
      false
    );
  };

  useEffect(() => {
    loadUsersList();
  }, [pageNumberState, userSearchValue, selectedGroupId]);

  useEffect(() => {
    //validate whole data!
    if (newValidatedUsers) {
      enableOrDisableImportButton();
    }
  }, [newValidatedUsers]);
  useEffect(() => {
    if (newValidatedUsers) {
      processAndImportValidatedUsers();
    }
  }, [importValidatedUsers]);
  const groupList = useMemo(() => {
    if (!groups) return [];
    return groups?.items.map((group) => ({ label: group.name, value: group.id, isAdmin: false }));
  }, [groups]);

  const tableData = useMemo(() => {
    return result && result.items ? result.items : [];
  }, [result, groupDetail]);

  const selectedPage = useMemo(() => {
    return seletedTableItemsByPage.find((item) => item.pageNumber === pageNumberState);
  }, [seletedTableItemsByPage, pageNumberState]);

  const tableValidationData = useMemo(() => {
    if (!newValidatedUsers) return [];
    else {
      return (
        groupList &&
        newValidatedUsers &&
        newValidatedUsers.map((usr: ValidatedUsersResponse) => {
          if (!usr.selectedNotifications) {
            const notifications = usr.alertNotification?.split(',') || [];
            const email = notifications.find((n) => n.trim() === 'Email');
            const text = notifications.find((n) => n.trim() === 'Text');
            const selected: DropDownItem[] = [];
            if (email) selected.push({ value: '1', label: email });
            if (text) selected.push({ value: '2', label: text });
            if (selected.length > 0) usr.selectedNotifications = selected;
          }

          return {
            ...usr,
            groups: groupList,
            selectedNotifications: usr.selectedNotifications,
            role: [
              { value: '1', label: 'Admin' },
              { value: '2', label: 'Member' }
            ],
            alertNotifications: [
              { value: '1', label: 'Email' },
              { value: '2', label: 'Text' }
            ]
          };
        })
      );
    }
  }, [newValidatedUsers]);

  const searchInputValue = (inputValue?: string, searchType?: string) => {
    if (inputValue?.includes('%') || inputValue?.includes('_')) {
      return setUserSearchValue('undefined');
    }
    if (searchType === 'global') {
      onPageChange(0);
      setPageNumber(0);
      setUserSearchValue(inputValue || '');
    }
  };

  useMemo(() => {
    if (group_id) {
      const searchOptions = [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Member', value: 'member' },
        { label: 'Admin', value: 'admin' }
      ];
      dispatch(tanStackTableActions.addFilterOptions(searchOptions));
    } else {
      const groupOptions = groupData?.items.map((group: UserGroup) => ({
        value: group.id,
        label: group.name
      }));
      dispatch(tanStackTableActions.addFilterOptions(groupOptions || []));
    }
  }, [groupData, group_id]);

  // To set the page number to 0 and clear search when tab changes.
  useEffect(() => {
    setPageNumber(0);
    setGroupId('');
    setShowImportUserBar?.(false);
  }, []);

  useEffect(() => {
    setPageNumber(pageNumber);
  }, [pageNumber]);

  const handleGroupSearch = (val?: string) => {
    if (val === 'All') {
      setSelectedGroupId('');
    } else setSelectedGroupId(val || '');
  };

  const handleClickRow = (action?: string, data?: UserTypes) => {
    if (action === 'selectUser' && data) {
      const inPageSelection = seletedTableItemsByPage.find(
        (item) => item.pageNumber === pageNumberState
      );
      if (!inPageSelection) {
        setSeletedTableItemsByPage([
          ...seletedTableItemsByPage,
          {
            selectedItems: [data as UserTypes],
            pageNumber: pageNumberState,
            isPartialSelection: true
          }
        ]);
      } else {
        const updatedSelection = seletedTableItemsByPage.map((item) => {
          if (item.pageNumber === pageNumberState) {
            const itemExists = item.selectedItems?.find((user) => user.id === data.id);
            if (!itemExists) {
              const newSelectedItems = [...(item.selectedItems || []), data];
              const isPartial = newSelectedItems.length < PAGE_LENGTH.SMALL;
              return {
                ...item,
                selectedItems: newSelectedItems,
                isPartialSelection: isPartial
              } as PageSelection;
            } else {
              return {
                ...item,
                selectedItems: item.selectedItems?.filter((user) => user.id !== data.id),
                isPartialSelection: true
              } as PageSelection;
            }
          }
          return item;
        });
        setSeletedTableItemsByPage(updatedSelection);
      }
    } else if (action === 'changeStatus' && data) {
      const params = { id: data.id, isActive: data.isActive };
      updateUserStatus([params]);
    } else if (action === 'selectAll') {
      const inPageSelection = seletedTableItemsByPage.find(
        (item) => item.pageNumber === pageNumberState
      );
      if (!inPageSelection) {
        setSeletedTableItemsByPage([
          ...seletedTableItemsByPage,
          { selectedItems: tableData, pageNumber: pageNumberState, isPartialSelection: false }
        ]);
      } else {
        const updatedSelection = seletedTableItemsByPage.map((item) => {
          if (item.pageNumber === pageNumberState) {
            return { ...item, selectedItems: tableData, isPartialSelection: false };
          }
          return item;
        });
        setSeletedTableItemsByPage(updatedSelection);
      }
    } else if (action === 'deselectAll') {
      const inPageSelection = seletedTableItemsByPage.find(
        (item) => item.pageNumber === pageNumberState
      );
      if (inPageSelection) {
        const updatedSelection = seletedTableItemsByPage.map((item) => {
          if (item.pageNumber === pageNumberState) {
            return { ...item, selectedItems: [], isPartialSelection: true };
          }
          return item;
        });
        setSeletedTableItemsByPage(updatedSelection);
      }
    }
  };
  const handleRoleSelect = (id: string, option?: OptionType, parentOption?: OptionType): void => {
    setNewValidatedUsers([
      ...(updateSelect(newValidatedUsers, id, option, parentOption) as ValidatedUsersResponse[])
    ]);
  };
  const handleMultiSelect = (field: string, items: MultiValue<DropDownItem>, id: string): void => {
    setNewValidatedUsers([
      ...(updateMultiSelect(newValidatedUsers, field, items, id) as ValidatedUsersResponse[])
    ]);
  };

  const validateOnBlur = (field: string, value: string, id: string): void => {
    let msg = '';
    switch (field) {
      case 'first_name':
      case 'last_name':
        msg = validateName(value);
        updateBulkValidationError(msg, field, id);
        break;
      case 'email':
        validateEmail(value).then((res) => {
          msg = res;
          updateBulkValidationError(msg, field, id);
        });
        break;
      case 'phone':
        msg = value && /^[0-9+\-()]+$/.test(value) ? '' : value ? 'Phone is not valid' : '';
        updateBulkValidationError(msg, field, id);
        break;
    }
  };
  const enableOrDisableImportButton = (): void => {
    let hasError = false;
    for (const user of newValidatedUsers as ValidatedUsersResponse[]) {
      if (user.errorMessages) {
        for (const error of user.errorMessages) {
          if (error.errors[0].length > 0) {
            setDisableImportUserButton?.(true);
            hasError = true;
            break;
          }
        }
        if (hasError) {
          break;
        }
      }
      if (!hasError && (!user.selectedGroups || user.selectedGroups.length === 0)) {
        setDisableImportUserButton?.(true);
        hasError = true;
        break;
      }
    }
    if (!hasError) {
      setDisableImportUserButton?.(false);
    }
  };
  const updateFieldOnChange = (field: string, value: string, pos: number, id: string): void => {
    setNewValidatedUsers([
      ...(updateValidatedUser(newValidatedUsers, field, value, pos, id) as ValidatedUsersResponse[])
    ]);
  };
  const handleCheckClickOnBulkMode = (action?: string, data?: UserTypes): void => {
    if (action === 'selectAll') {
      setSelectedTableItems(tableValidationData as unknown as UserTypes[]);
    } else if (action === 'deselectAll') {
      setSelectedTableItems([]);
    } else {
      const isAlreadySelected = selectedTableItems?.find((user) => user?.id === data?.id);
      if (isAlreadySelected) {
        const keepSelected = selectedTableItems?.filter((user) => user?.id !== data?.id);
        setSelectedTableItems(keepSelected);
      } else if (selectedTableItems && data) {
        setSelectedTableItems([...selectedTableItems, data]);
      }
    }
  };
  //  Add, edit, delete action
  const handleTableAction = ({ flyoutType, flyoutItemId, action, selectedUser }: TableAction) => {
    if (action === 'addUser' || action === 'editUser') {
      onClickButton(flyoutType, flyoutItemId, selectedUser);
    } else if (action === 'delete') {
      setShowDeleteConfirmation(true);
      return;
    } else if (action === 'import') {
      setShowImportUsersModal(true);
    } else if (action === 'edit') {
      setActiveUserData(selectedUser);
      setShowEditModal(true);
      return;
    } else if (action === 'exit') {
      setShowExitImportModal(true);
      return;
    } else if (action === 'download') {
      downloadImportTemplate();
      return;
    } else {
      return;
    }
  };
  const downloadImportTemplate = (): void => {
    try {
      downloadTemplateFile()
        .unwrap()
        .then((res) => {
          if (res && res.documentPath) {
            saveAs(res.documentPath, `userImportTemplate.xlsx`);
          } else {
            ToastMsg({
              heading: 'Error',
              type: 'error',
              message: 'Unable to download user template file!'
            });
          }
        });
    } catch (err) {
      ToastMsg({
        heading: 'Error',
        type: 'error',
        message: 'Unable to download user template file!'
      });
    }
  };
  const validatedUsersWithKeys = addKeyProp(
    tableValidationData,
    UserManagementTableType.USER
  ) as BulkUser[];
  const processUploadedFile = (file: File | null): void => {
    uploadUsersFile({
      file: file as File
    })
      .unwrap()
      .then((res) => {
        if (res.length === 0) {
          ToastMsg({
            message: t('No user data found in the file'),
            heading: t('warning') as string,
            position: 'top-center',
            type: 'warning'
          });
        } else {
          ToastMsg({
            message: t('User file upload successful'),
            heading: t('complete') as string,
            position: 'top-center',
            type: 'success'
          });
          const data = userNameValidation(res as ValidatedUsersResponse[]);
          setNewValidatedUsers(addIdProp(data) as ValidatedUsersResponse[]);
          setIsBulkImportMode(true);
          setShowImportUserBar?.(true);
          setIsActiveUserImportMode?.(true);
        }
        setShowImportUsersModal(false);
        setSelectedTableItems([]);
      })
      .catch((error) => {
        ToastMsg({
          message: 'Failed to upload file',
          heading: 'Error',
          position: 'top-center',
          type: 'error'
        });
        console.error(error?.data?.detail || error);
      });
  };
  const processAndImportValidatedUsers = (): void => {
    importValidateUsers(processImportPayload())
      .unwrap()
      .then(() => {
        setIsBulkImportMode(false);
        setIsActiveUserImportMode?.(false);
        setDisableImportUserButton?.(true);
        setShowImportUserBar?.(false);
        ToastMsg({
          message: t('User data has been successfully imported'),
          heading: t('complete') as string,
          position: 'top-center',
          type: 'success'
        });
        loadUsersList();
      })
      .catch((error) => {
        ToastMsg({
          message: 'Failed to import user data',
          heading: 'Error',
          position: 'top-center',
          type: 'error'
        });
        console.error(error?.data?.detail || error);
      });
  };
  const processImportPayload = (): UserTypes[] => {
    if (!newValidatedUsers) return [];
    const data: UserTypes[] = [];
    const val: UserTypes = { groups: [] };
    newValidatedUsers.forEach((user) => {
      const tempGroup: UserGroup[] = [];
      val.email = user.email;
      val.firstName = user.firstName;
      val.lastName = user.lastName;
      val.phone = user.phone || '';
      val.createdAt = new Date().getDate().toLocaleString() as string;
      val.updatedAt = new Date().getDate().toLocaleString() as string;
      val.isActive = true;
      val.ssoAuthentication = true;
      val.emailAlert = user.selectedNotifications?.some(
        (notification) => notification.label === 'Email'
      );
      val.textAlert = user.selectedNotifications?.some(
        (notification) => notification.label === 'Text'
      );
      if (user.selectedGroups) {
        for (const group of user.selectedGroups as DropDownItem[]) {
          let gdata: UserGroup = {
            name: '',
            isAdmin: false,
            id: '',
            isTextAlert: false,
            isEmailAlert: false
          };
          gdata = {
            name: group.label,
            id: group.value as string,
            isAdmin: group.isAdmin
          };
          tempGroup.push(gdata);
        }
      }

      val.groups = tempGroup;
      data.push(JSON.parse(JSON.stringify(val)));
    });
    return data;
  };
  const validateEmail = async (email: string): Promise<string> => {
    const emailRegex =
      /^(?=[^@]*[A-Za-z])[a-zA-Z0-9]+(?:[._-][a-zA-Z0-9]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,4}$/i;

    const emailRegexEmojiRes = /^(?!.*[\uD800-\uDFFF]).*$/;
    if (email === '') {
      return `Email cannot be empty`;
    } else if (!emailRegex.test(email as string) || !emailRegexEmojiRes.test(email as string)) {
      return `Email not valid`;
    } else {
      // Check if email is available/taken by another user
      try {
        await validateUserEmail({ email: email as string })
          .unwrap()
          .then(() => {
            return '';
          });
      } catch (error) {
        return `Email already in use`;
      }
    }

    return '';
  };

  const validateName = (name: string): string => {
    if (name.length >= 35) return 'Name should be less that 35 letters';
    if (!alphaNumericNameWithSpaceAndHyphen(name)) return 'Name is not valid';
    return '';
  };
  const updateBulkValidationError = (msg: string, field: string, id: string): void => {
    const temRes = JSON.parse(JSON.stringify(newValidatedUsers)) as ValidatedUsersResponse[];
    let isExistingValidation = false;
    temRes.forEach((res) => {
      if (res.id === id && res.errorMessages !== null) {
        for (const error of res.errorMessages) {
          if (error.columnName === field) {
            error.errors[0] = msg;
            isExistingValidation = true;
            break;
          }
        }
        if (!isExistingValidation) {
          res.errorMessages.push({ columnName: field, errors: [msg] });
        }
      } else if (res.id === id && res.errorMessages === null && msg.length > 0) {
        res.errorMessages = [{ columnName: field, errors: [msg] }];
      }
    });
    setNewValidatedUsers([...temRes]);
  };
  const handleDeleteActionOnImport = (): void => {
    setShowDeleteConfirmation(false);
    const tempUsers: ValidatedUsersResponse[] = JSON.parse(JSON.stringify(newValidatedUsers));
    const selectedIds = selectedTableItems.map((i) => i.id);
    for (let i = 0; i < tempUsers.length; i++) {
      if (selectedIds.includes(tempUsers[i].id)) {
        tempUsers.splice(i, 1);
        i--; //reset loop counter to match new array after slice
      }
    }
    setNewValidatedUsers([...tempUsers]);
    setSelectedTableItems([]);
  };
  const dataWithKeys = transformUserColumnOptions(tableData, UserManagementTableType.USER);

  const closeBackModal = (): void => {
    setShowExitImportModal(false);
    setMoveToGroupOnImportMode?.('cancel');
  };
  const exitImportUserPage = (): void => {
    setShowExitImportModal(false);
    setIsBulkImportMode(false);
    setIsActiveUserImportMode?.(false);
    setMoveToGroupOnImportMode?.('continue');
    setShowImportUserBar?.(false);
  };
  const afterDeleteHouseKeeping = (): void => {
    setShowDeleteConfirmation(false);
    setSeletedTableItemsByPage((prev) =>
      prev.map((item) => {
        if (item.pageNumber === pageNumberState) {
          return { ...item, selectedItems: [], isPartialSelection: true };
        }
        return item;
      })
    );
  };

  const isItemSelected = !!selectedTableItems.length || !!selectedPage?.selectedItems?.length;

  const userTableButtons = useMemo(() => {
    return [
      {
        label: `${t('add_user')}`,
        icon: AddUserIcon,
        onClick: () =>
          handleTableAction({
            flyoutType: UserManagementTableType.USER,
            selectedUser: [] as unknown as UserTypes,
            action: 'addUser'
          })
      },
      {
        label: `${t('import')}`,
        icon: ImportIcon,
        onClick: () =>
          handleTableAction({
            flyoutType: UserManagementTableType.USER,
            selectedUser: [] as unknown as UserTypes,
            action: 'import'
          })
      },
      {
        label: `${t('user_template')}`,
        icon: DownloadIcon,
        onClick: () =>
          handleTableAction({
            flyoutType: UserManagementTableType.USER,
            selectedUser: [] as unknown as UserTypes,
            action: 'download'
          })
      },
      {
        label: `${t('delete')}`,
        icon: DeleteIcon,
        disable: !isItemSelected,
        textColor: themeColors.error200,
        onClick: () => {
          isItemSelected &&
            handleTableAction({
              flyoutType: UserManagementTableType.USER,
              selectedUser: [] as unknown as UserTypes,
              action: 'delete'
            });
        }
      }
    ];
  }, [isItemSelected]);

  const userTableImportButtons = useMemo(() => {
    return [
      {
        label: `${t('cancel')}`,
        icon: ImportIcon,
        textColor: 'black',
        onClick: () =>
          handleTableAction({
            flyoutType: UserManagementTableType.USER,
            selectedUser: [] as unknown as UserTypes,
            action: 'exit'
          })
      },
      {
        label: `${t('delete')}`,
        icon: DeleteIcon,
        textColor: themeColors.error200,
        disable: !isItemSelected,
        onClick: () =>
          handleTableAction({
            flyoutType: UserManagementTableType.USER,
            selectedUser: [] as unknown as UserTypes,
            action: 'delete'
          })
      }
    ];
  }, [isItemSelected]);

  return (
    <>
      {isBulkImportMode ? (
        <DisplayImportedUsers
          isLoading={isLoading}
          tableValidationData={validatedUsersWithKeys || []}
          handleRoleOptionSelect={handleRoleSelect}
          handleMultiSelect={handleMultiSelect}
          validateOnBlur={validateOnBlur}
          t={t}
          updateFieldOnChange={updateFieldOnChange}
          handleCheckClickOnBulkMode={handleCheckClickOnBulkMode}
          selectedTableItems={selectedTableItems}
          defaultUserSortState={defaultUserSortState}
          tableHeaderButtons={userTableImportButtons}
        />
      ) : (
        <DisplayExistingUsers
          isLoading={isLoading}
          dataWithKeys={dataWithKeys}
          handleTableAction={handleTableAction}
          t={t}
          handleClickRow={handleClickRow}
          defaultUserSortState={defaultUserSortState}
          searchInputValue={searchInputValue}
          handleGroupSearch={handleGroupSearch}
          result={result}
          onPageChange={onPageChange}
          pageLength={PAGE_LENGTH.SMALL}
          pageNumberState={pageNumberState}
          pageNumber={pageNumber}
          tableHeaderButtons={userTableButtons}
          isGroupPage={isGroupPage}
          selectedPage={selectedPage}
        />
      )}

      {showImportUsersModal && (
        <ImportUsers
          onFileChange={processUploadedFile}
          setShowImportUsersModal={setShowImportUsersModal}
        />
      )}
      {showDeleteConfirmation &&
        (selectedTableItems?.length || selectedPage?.selectedItems?.length) && (
          <DeleteUser
            showConfirmation={showDeleteConfirmation}
            closeModal={() => {
              setShowDeleteConfirmation(false);
            }}
            houseKeepOnDelete={afterDeleteHouseKeeping}
            data={
              (selectedTableItems.length > 0 && selectedTableItems) ||
              (selectedPage?.selectedItems as UserTypes[]) ||
              []
            }
            groupId={group_id}
            handleDeleteActionOnImport={handleDeleteActionOnImport}
            isImportMode={isBulkImportMode}
          />
        )}

      {showEditModal ? (
        <UpdateUser
          showEditModal={showEditModal}
          closeModal={() => {
            setShowEditModal(false);
          }}
          data={activeUserData}
        />
      ) : (
        ''
      )}
      <Modal
        title={
          <StyledModalHeaderWrapper>
            <ModalIconWrapper>
              <IcoWarningNew />
              <StyledTitle>Warning</StyledTitle>
            </ModalIconWrapper>
            <ModalCloseButton>
              <CloseIcon onClick={closeBackModal}>{IcoClose()}</CloseIcon>
            </ModalCloseButton>
          </StyledModalHeaderWrapper>
        }
        allowContentScroll
        onClose={closeBackModal}
        showCloseButton={false}
        size={ModalSize.XSMALL}
        visible={showExitImportModal || (moveToGroupOnImportMode as boolean)}
      >
        <ModalContent>
          <AlertBody>
            Are you sure you want to navigate away from this page? All the changes will be lost.
          </AlertBody>

          <Footer>
            <StyledButtonRow>
              <Button
                onClick={closeBackModal}
                variant="link"
                borderRadius="3rem"
                borderColor={theme.colors.text.error}
                color={theme.colors.text.error}
              >
                Cancel
              </Button>
              <Button
                onClick={exitImportUserPage}
                borderColor={theme.colors.text.error}
                variant="constant"
                bgColor={theme.colors.text.error}
                borderRadius="3rem"
                color={theme.colors.white}
              >
                Continue
              </Button>
            </StyledButtonRow>
          </Footer>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UserManagementTable;
